import React, { useCallback, useMemo, useRef } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { SCHEDULE_POST_MODAL } from "../../../../constants/modal.constant";
import { useFacebookCore } from "../../../../cores/facebook";
import { useGlobalCore } from "../../../../cores/globals";
import { useInstagramCore } from "../../../../cores/instagram";
import { useLinkedInCore } from "../../../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../../../cores/linkedInBusiness";
import { useMeCore } from "../../../../cores/me";
import { useSocialPostCore } from "../../../../cores/socialPost";
import {
  SocialProviderId,
  normalizeSocialProviderIdForPreview,
} from "../../../../cores/socialPost/config";
import { SOCIAL_ACTION_TYPES } from "../../../../cores/socialPost/consts/socialActionType.constant";
import { SOCIAL_POST_TYPES } from "../../../../cores/socialPost/consts/socialPostType.constant";
import { loadPostImagesFromMediaUrls } from "../../../../cores/socialPost/helpers/loadPostMediaFromUrls";
import { useTwitterCore } from "../../../../cores/twitter";
import { useTiktokCore } from "../../../../cores/tiktok";
import { PostActionButtons } from "./PostActionButtons";
import "./ModernPostButtons.scss";

interface Props {
  from: string;
  repost: boolean;
  onReset: () => void;
  scheduleAt?: string;
  /** Calendario: validar fecha/hora sin parsear el string (mismo instante que el composer). */
  scheduleAtTimestamp?: number;
  scheduleDirty?: boolean;
  onScheduledSuccess?: () => void;
  onSavedSuccess?: () => void;
  onDeletedSuccess?: () => void;
  /** Tras programar o duplicar con éxito, recarga la grilla del calendario. */
  onPostsRefresh?: () => void;
  /** Calendario: al duplicar, pasar a modo programación (día/hora) con el mismo contenido. */
  onBeginDuplicateSchedule?: () => void;
}

export const ModernPostButtons = ({
  from,
  repost,
  onReset,
  scheduleAt,
  scheduleAtTimestamp,
  scheduleDirty = false,
  onScheduledSuccess,
  onSavedSuccess,
  onDeletedSuccess,
  onPostsRefresh,
  onBeginDuplicateSchedule,
}: Props) => {
  const lastActionRef = useRef<string | null>(null);

  const intl = useIntl();
  const {
    socialPost: socialPostTwitter,
    socialPostVideo: socialPostVideoTwitter,
  } = useTwitterCore();
  const {
    socialPost: socialPostFacebook,
    socialPostVideo: socialPostVideoFacebook,
  } = useFacebookCore();
  const {
    socialPost: socialPostTiktok,
    socialPostVideo: socialPostVideoTiktok,
  } = useTiktokCore();
  const { socialPost: socialPostLinkedIn } = useLinkedInCore();
  const {
    socialPost: socialPostLinkedInBusiness,
    socialPostVideo: socialPostVideoLinkedInBusiness,
  } = useLinkedInBusinessCore();
  const {
    socialPost: socialPostInstagram,
    socialPostVideo: socialPostVideoInstagram,
    cleanErrors,
  } = useInstagramCore();
  const { company, companyPlan } = useMeCore();
  const { turnOnModal } = useGlobalCore();
  const {
    socialProviders,
    postErrors,
    images,
    video,
    text = "",
    mentions,
    setSocialAction,
    selectedPost,
    duplicateScheduleSourcePostId,
    setDuplicateScheduleSourcePostId,
  } = useSocialPostCore();

  const canSchedule = useMemo(
    () => companyPlan?.rules.allowScheduledPost,
    [companyPlan]
  );

  const hasInstagramSelected = useMemo(() => {
    return (
      socialProviders.find(
        (sp: any) =>
          sp?.id === SocialProviderId.Instagram ||
          sp?.name === SocialProviderId.Instagram
      ) != null
    );
  }, [socialProviders]);

  const instagramScheduleOk = useMemo(() => {
    if (!hasInstagramSelected) return true;
    // Requisito: Instagram solo se agenda con al menos 1 imagen (subida o ya guardada en el post).
    if (images.length > 0) return true;
    return (selectedPost?.media?.length ?? 0) > 0;
  }, [hasInstagramSelected, images.length, selectedPost?.media?.length]);

  const disabled = useMemo(() => {
    const socialProviderIds = socialProviders.map((sp) => sp.id);
    const hasError =
      postErrors.find((error) =>
        socialProviderIds.includes(error.socialProviderId)
      ) != null;
    const hasMediaInDraft =
      images.length > 0 ||
      !!video ||
      (selectedPost?.media && selectedPost.media.length > 0);
    const hasText = Boolean((text || "").trim() || selectedPost?.post?.text?.trim());
    const emptyContent = !hasText && !hasMediaInDraft;
    const noNetworks = socialProviders.length === 0;
    return hasError || emptyContent || noNetworks;
  }, [
    postErrors,
    images.length,
    text,
    video,
    selectedPost?.media,
    selectedPost?.post?.text,
    socialProviders.length,
  ]);

  const onSuccess = useCallback(() => {
    setDuplicateScheduleSourcePostId(null);
    onReset();
    cleanErrors();
    if (
      from === "calendar" &&
      lastActionRef.current === SOCIAL_ACTION_TYPES.SCHEDULE
    ) {
      onPostsRefresh?.();
      onScheduledSuccess?.();
    }
  }, [
    setDuplicateScheduleSourcePostId,
    onReset,
    cleanErrors,
    from,
    onPostsRefresh,
    onScheduledSuccess,
  ]);

  const onOpeningSchedule = useCallback(() => {
    if (!disabled && canSchedule && instagramScheduleOk) {
      turnOnModal(SCHEDULE_POST_MODAL, true);
    }
  }, [turnOnModal, disabled, canSchedule, instagramScheduleOk]);

  const send = useCallback(
    async (
      parseDate: string,
      actionType: string,
      duplicateFromPostId?: string
    ) => {
      lastActionRef.current = actionType;
      setSocialAction(actionType);

      let repostId = "";
      if (duplicateFromPostId) {
        repostId = duplicateFromPostId;
      } else if (
        duplicateScheduleSourcePostId &&
        selectedPost?.post?.id &&
        duplicateScheduleSourcePostId === selectedPost.post.id
      ) {
        repostId = duplicateScheduleSourcePostId;
      } else if (repost && selectedPost?.post?.id) {
        repostId = selectedPost.post.id;
      }

      if (socialProviders.length === 0) {
        toast.error(
          intl.formatMessage({
            id: "app.aiGeneration.postToSocialSelectNetworkHint",
            defaultMessage: "Seleccioná al menos una red social para publicar.",
          })
        );
        return;
      }

      /** Vista previa usa `selectedPost.media` si no hay blobs en Redux; la API solo recibía `images` vacío. */
      const effectiveText =
        (text ?? "").trim() ||
        (selectedPost?.post?.text ?? "").trim();
      let imagesPayload = images;
      const needsHydrateFromUrls =
        !video &&
        images.length === 0 &&
        (selectedPost?.media?.length ?? 0) > 0 &&
        selectedPost?.post?.type !== SOCIAL_POST_TYPES.VIDEO;

      const prepToastId = "social-schedule-prepare-media";
      if (needsHydrateFromUrls) {
        toast.info(
          intl.formatMessage({
            id: "app.socialCalendar.preparingMedia",
            defaultMessage: "Preparando imágenes…",
          }),
          { toastId: prepToastId, autoClose: false }
        );
        try {
          const media = selectedPost?.media;
          if (!media?.length) {
            toast.dismiss(prepToastId);
            return;
          }
          imagesPayload = await loadPostImagesFromMediaUrls(media);
        } catch (e) {
          console.error(e);
          toast.dismiss(prepToastId);
          toast.error(
            intl.formatMessage({
              id: "app.socialMediaPost.errorMsg12",
              defaultMessage:
                "Se necesita una Imagen o Video para publicar en esta red social",
            })
          );
          return;
        }
        toast.dismiss(prepToastId);
      }

      /* Sin repostId la API necesita binarios; con duplicar/agenda desde origen el backend puede resolver la media. */
      if (
        hasInstagramSelected &&
        !video &&
        imagesPayload.length === 0 &&
        !repostId
      ) {
        toast.error(
          intl.formatMessage({
            id: "app.socialMediaPost.errorMsg12",
            defaultMessage:
              "Se necesita una Imagen o Video para publicar en esta red social",
          })
        );
        return;
      }

      socialProviders.forEach((sp) => {
        const providerKey = normalizeSocialProviderIdForPreview(
          String(sp.id ?? sp.name ?? "").trim() || undefined
        );
        switch (providerKey) {
        case SocialProviderId.Twitter:
          video
            ? socialPostVideoTwitter(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostTwitter(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        case SocialProviderId.TikTok:
          video
            ? socialPostVideoTiktok(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostTiktok(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        case SocialProviderId.LinkedInBusiness:
          video
            ? socialPostVideoLinkedInBusiness(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostLinkedInBusiness(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        case SocialProviderId.LinkedIn:
          socialPostLinkedIn(
            company?.id!,
            effectiveText,
            mentions,
            imagesPayload,
            parseDate,
            repostId,
            onSuccess
          );
          break;
        case SocialProviderId.Facebook:
          video
            ? socialPostVideoFacebook(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostFacebook(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        case SocialProviderId.Instagram:
          video
            ? socialPostVideoInstagram(
                company?.id!,
                effectiveText,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostInstagram(
                company?.id!,
                effectiveText,
                mentions,
                imagesPayload,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        default:
          if (process.env.NODE_ENV === "development") {
            // Antes: solo sp.name; id "instagram" o name "LinkedIn Business" no coincidían y no se enviaba nada.
            console.warn("[ModernPostButtons] Proveedor sin handler de envío:", sp, "→", providerKey);
          }
          break;
      }
    });
    },
    [
      socialProviders,
      video,
      mentions,
      company,
      text,
      repost,
      images,
      selectedPost?.post?.id,
      selectedPost?.post?.text,
      selectedPost?.post?.type,
      selectedPost?.media,
      socialPostVideoTwitter,
      socialPostTwitter,
      socialPostVideoLinkedInBusiness,
      socialPostLinkedInBusiness,
      socialPostLinkedIn,
      socialPostVideoFacebook,
      socialPostFacebook,
      socialPostVideoInstagram,
      socialPostInstagram,
      socialPostTiktok,
      socialPostVideoTiktok,
      onSuccess,
      setSocialAction,
      duplicateScheduleSourcePostId,
      intl,
      hasInstagramSelected,
    ]
  );

  const postNow = useCallback(() => {
    void send("", SOCIAL_ACTION_TYPES.POST).catch((e) => {
      console.error(e);
      toast.dismiss("social-schedule-prepare-media");
    });
  }, [send]);

  const canScheduleAt = useMemo(() => {
    if (typeof scheduleAtTimestamp === "number" && !Number.isNaN(scheduleAtTimestamp)) {
      return scheduleAtTimestamp > Date.now() - 750;
    }
    if (!scheduleAt) return false;
    const t = moment(scheduleAt).valueOf();
    return !Number.isNaN(t) && t > Date.now() - 750;
  }, [scheduleAt, scheduleAtTimestamp]);

  const scheduleNow = useCallback(() => {
    if (!scheduleAt) return;
    if (!canScheduleAt) return;
    if (!instagramScheduleOk) return;
    void send(scheduleAt, SOCIAL_ACTION_TYPES.SCHEDULE).catch((e) => {
      console.error(e);
      toast.dismiss("social-schedule-prepare-media");
    });
  }, [scheduleAt, canScheduleAt, instagramScheduleOk, send]);

  const getSelectedProvidersText = () => {
    if (socialProviders.length === 0) return "";
    if (socialProviders.length === 1) return socialProviders[0].displayName;
    return intl.formatMessage({ id: "app.socialPostSelector.selectedNetworks" }, { count: socialProviders.length });
  };

  return (
    <div className="modern-post-buttons">
      {/* Selected providers info */}
      <div className="selected-providers-info">
        <span className="providers-label">{intl.formatMessage({ id: "app.socialPostSelector.willPublishOn" })}</span>
        <div className="providers-tags">
          {socialProviders.map((provider) => (
            <div key={provider.id} className="provider-tag">
              <span className="provider-name">{provider.displayName}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="action-buttons">
        {selectedPost && !repost && !duplicateScheduleSourcePostId ? (
          <PostActionButtons
            from={from}
            save={false}
            disabled={disabled}
            onReset={onReset}
            onSavedSuccess={onSavedSuccess}
            onDeletedSuccess={onDeletedSuccess}
            onDuplicateSchedule={onBeginDuplicateSchedule}
            onPostsRefresh={onPostsRefresh}
          />
        ) : (
          <>
            <button
              className={`schedule-button ${!canSchedule ? 'disabled' : ''}`}
              disabled={
                disabled ||
                !canSchedule ||
                (from === "calendar" ? !canScheduleAt : false) ||
                !instagramScheduleOk
              }
              onClick={from === "calendar" ? scheduleNow : onOpeningSchedule}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{intl.formatMessage({ id: "app.socialMediaPost.program" })}</span>
            </button>
            
            <button
              className="publish-button"
              disabled={
                disabled ||
                (from === "calendar"
                  ? scheduleDirty && !duplicateScheduleSourcePostId
                  : false) ||
                !instagramScheduleOk
              }
              onClick={postNow}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>
                {from === "calendar"
                  ? "Publicar ahora"
                  : intl.formatMessage({ id: "app.socialMediaPost.toPost" })}
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};
